// Constantes e tipos do Motor Comercial Integrado (visão consolidada CRM + Radar).

export interface OportunidadeConsolidada {
  id: string;
  origem: 'crm' | 'radar';
  crm_id: number | null;
  radar_id: number | null;
  titulo: string;
  empresa: string | null;
  empresa_id: number | null;
  cidade: string | null;
  uf: string | null;
  valor: number | null;
  custo_total: number | null;
  lucro: number | null;
  margem: number | null;
  probabilidade: number | null;
  pipeline_ponderado: number | null;
  lucro_ponderado: number | null;
  faixa: string;
  margem_tone: 'verde' | 'amarela' | 'vermelha' | null;
  etapa: string | null;
  ganha: boolean;
  perdida: boolean;
  aberta: boolean;
  fonte: string | null;
  solucoes: string[];
  responsavel: string | null;
  proxima_acao: string | null;
  proxima_acao_em: string | null;
  previsao_fechamento: string | null;
  data_fechamento: string | null;
  custos: Record<string, number> | null;
  origem_dados: any;
  confianca: string | null;
  hop_score: number | null;
  projeto_estrategico_id: number | null;
  created_at: string | null;
}

export interface ComercialKpis {
  pipeline: number;
  pipeline_ponderado: number;
  lucro_potencial: number;
  lucro_ponderado: number;
  quantidade: number;
  quantidade_abertas: number;
  ganhas: number;
  ticket_medio: number | null;
  margem_media: number | null;
  sem_proxima_acao: number;
  propostas_sem_resposta: number;
  previsao_30: number;
  previsao_60: number;
  previsao_90: number;
}

export interface ConfigComercial {
  meta_margem: number;
  motor_caixa_min: number;
  motor_caixa_max: number;
  motor_crescimento_min: number;
  motor_crescimento_max: number;
  projeto_estrategico_min: number;
  regioes_prioritarias: { key: string; label: string; prioridade: number }[] | null;
  ajustes_geograficos: { cidade: string; uf: string; acao: string; motivo: string; ativa: boolean }[] | null;
}

export interface ProjetoEstrategico {
  id: number;
  titulo: string;
  empresa_id: number | null;
  empresa_nome: string | null;
  oportunidade_id: number | null;
  radar_oportunidade_id: number | null;
  valor_potencial: number | null;
  lucro_potencial: number | null;
  margem: number | null;
  probabilidade: number | null;
  cidade: string | null;
  uf: string | null;
  solucoes: string[] | null;
  estagio: string;
  previsao_fechamento: string | null;
  previsao_implantacao: string | null;
  decisor: string | null;
  arquiteto: string | null;
  construtora: string | null;
  especificador: string | null;
  concorrentes: string[] | null;
  proximos_passos: string[] | null;
  riscos: string[] | null;
  documentos: string[] | null;
  margem_tone: 'verde' | 'amarela' | 'vermelha' | null;
  faixa: string;
  created_at: string;
}

export interface Marco {
  id: number;
  projeto_id: number;
  titulo: string;
  tipo: string | null;
  data_prevista: string | null;
  data_concluida: string | null;
  concluido: boolean;
  observacao: string | null;
  created_at: string;
}

export const FAIXA_LABELS: Record<string, string> = {
  projeto_estrategico: 'Projeto Estratégico',
  motor_crescimento: 'Motor Crescimento',
  motor_caixa: 'Motor Caixa',
  abaixo: 'Abaixo do Motor Caixa',
};

export const FAIXA_TONES: Record<string, string> = {
  projeto_estrategico: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  motor_crescimento: 'bg-amber-50 text-amber-700 border-amber-200',
  motor_caixa: 'bg-green-50 text-green-700 border-green-200',
  abaixo: 'bg-gray-100 text-gray-500 border-gray-200',
};

export const FAIXA_ICONS: Record<string, string> = {
  projeto_estrategico: 'ri-focus-3-line',
  motor_crescimento: 'ri-line-chart-line',
  motor_caixa: 'ri-cash-line',
  abaixo: 'ri-arrow-down-line',
};

export const MARGEM_TONES: Record<string, { label: string; tone: string; icon: string }> = {
  verde: { label: 'Margem saudável', tone: 'bg-green-50 text-green-700 border-green-200', icon: 'ri-check-double-line' },
  amarela: { label: 'Atenção', tone: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ri-alert-line' },
  vermelha: { label: 'Margem baixa', tone: 'bg-red-50 text-red-600 border-red-200', icon: 'ri-close-circle-line' },
};

export const PAPEL_EMPRESA_LABELS: Record<string, string> = {
  cliente: 'Cliente',
  prospect: 'Prospect',
  construtora: 'Construtora',
  arquiteto_escritorio: 'Arquiteto / Escritório',
  designer_especificador: 'Designer / Especificador',
  incorporadora: 'Incorporadora',
  fornecedor: 'Fornecedor',
  fabrica: 'Fábrica',
  distribuidor: 'Distribuidor',
  parceiro: 'Parceiro',
  prestador: 'Prestador',
  orgao_publico: 'Órgão público',
  influenciador: 'Influenciador',
  concorrente: 'Concorrente',
  outro: 'Outro',
};

export const NIVEL_DECISAO_LABELS: Record<string, string> = {
  decisor: 'Decisor',
  influenciador: 'Influenciador',
  especificador: 'Especificador',
  comprador: 'Comprador',
  usuario: 'Usuário',
  financeiro: 'Financeiro',
  diretoria: 'Diretoria',
  desconhecido: 'Desconhecido',
};

export const ORIGEM_LABELS: Record<string, string> = {
  arquiteto: 'Arquiteto',
  cliente: 'Cliente',
  fornecedor: 'Fornecedor',
  networking: 'Networking',
  evento: 'Evento',
  indicacao: 'Indicação',
  prospeccao: 'Prospecção',
  google: 'Google',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  outro: 'Outro',
};

export const ESTAGIOS_PROJETO: { key: string; label: string }[] = [
  { key: 'sinal', label: 'Sinal' },
  { key: 'descoberta', label: 'Descoberta' },
  { key: 'relacionamento', label: 'Relacionamento' },
  { key: 'briefing', label: 'Briefing' },
  { key: 'especificacao', label: 'Especificação' },
  { key: 'projeto', label: 'Projeto' },
  { key: 'cotacao', label: 'Cotação' },
  { key: 'proposta', label: 'Proposta' },
  { key: 'negociacao', label: 'Negociação' },
  { key: 'aprovacao', label: 'Aprovação' },
  { key: 'contratacao', label: 'Contratação' },
  { key: 'implantacao', label: 'Implantação' },
  { key: 'entrega', label: 'Entrega' },
  { key: 'pos_venda', label: 'Pós-venda' },
  { key: 'ganho', label: 'Ganho' },
  { key: 'perdido', label: 'Perdido' },
  { key: 'monitoramento', label: 'Monitoramento' },
];

export const ESTAGIO_LABELS: Record<string, string> = Object.fromEntries(
  ESTAGIOS_PROJETO.map((e) => [e.key, e.label]),
);

export const CUSTO_KEYS: { key: string; label: string }[] = [
  { key: 'produto', label: 'Produto' },
  { key: 'frete', label: 'Frete' },
  { key: 'montagem', label: 'Montagem' },
  { key: 'impostos', label: 'Impostos' },
  { key: 'terceirizacao', label: 'Terceirização' },
  { key: 'comissao', label: 'Comissão' },
  { key: 'financeiro', label: 'Financeiro' },
  { key: 'projetista', label: 'Projetista' },
  { key: 'deslocamento', label: 'Deslocamento' },
  { key: 'outros', label: 'Outros custos' },
  { key: 'reserva_risco', label: 'Reserva de risco' },
];

export function formatBRL(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}